import { ReactNode } from 'react';
import { To } from 'react-router-dom';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Avatar, AvatarProps } from '@components/avatar';
import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconFile } from '@components/icons/file';
import { PreviewFile, usePreviewFileModal } from '@components/modals/preview-file-modal';
import { ProgressBar } from '@components/progress-bar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip/tooltip';
import { getScopedTranslate, t, useScopedTranslate } from '@locale/translate';
import { getFileExtension } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';
import { sortStrings } from '@utils/sorting';
import { fileNameFromPath, formatPhoneNumber } from '@utils/strings';

import { ColumnDetailModalProps, showColumnDetailModal } from './column-detail-modal';
import { AvailableColumn, ColumnType, DataItemType } from './types';

export const rowHeightRaw = 56;
export const rowHeight = pxToRem(rowHeightRaw);
export const textColumnMaxLength = 255;

export const SimpleText = ({ text }: { text: string }) => <Text font="regular14" text={text} />;
export const PhoneNumber = ({ phoneNumber }: { phoneNumber: string }) => (
  <SimpleText text={formatPhoneNumber(phoneNumber)} />
);
export const Number = ({ value }: { value: number }) => <SimpleText text={formatNumber(value)} />;

const ensurePreviewFileArray = (
  value: PreviewFile | Array<PreviewFile> | string | Array<string>,
): Array<PreviewFile> => {
  const stringToPreviewFile = (value: string): PreviewFile => {
    return {
      src: value,
      name: fileNameFromPath(value),
    };
  };

  const objectToPreviewFile = (value: any): PreviewFile => {
    if (typeof value === 'string') return stringToPreviewFile(value);
    return {
      src: value.src,
      name: value.name || fileNameFromPath(value.src),
      srcConverted: value.src_converted ?? value.srcConverted,
    };
  };

  if (Array.isArray(value)) {
    return value.map(objectToPreviewFile);
  }
  return [objectToPreviewFile(value)];
};

const PREVIEWABLE_FILE_EXTENSIONS = ['pdf', 'png', 'jpeg', 'jpg', 'gif', 'webp', 'txt'];

const isFileConverting = (file: PreviewFile): boolean => {
  const extension = getFileExtension(file.name)?.toLowerCase();
  const needsConversion = !extension || !PREVIEWABLE_FILE_EXTENSIONS.includes(extension);
  return needsConversion && !file.srcConverted;
};

export const FileField = ({
  value,
  column,
  row,
}: {
  value: PreviewFile | Array<PreviewFile> | string | Array<string>;
  column: AvailableColumn<DataItemType>;
  row: DataItemType;
}) => {
  const showFilePreviewModal = usePreviewFileModal();
  const val = ensurePreviewFileArray(value);

  if (val.length === 1) {
    <IconButton
      size="md"
      variant="ghost-purple"
      icon={<IconFile />}
      onClick={() =>
        showFilePreviewModal({
          formFile: val[0],
          filesList: [val[0]],
        })
      }
    />;
  }

  const thresholdLength = 2;
  const isTooLong = column.onMultiFilesClick && val.length > thresholdLength;
  const list = isTooLong ? val.slice(0, thresholdLength) : val;

  return (
    <Stack space={1} orientation="horizontal">
      {list.map((file, index) => {
        const converting = isFileConverting(file);

        if (converting) {
          return (
            <Tooltip key={index} content={t('core.fileConverting')}>
              <IconButton size="md" variant="ghost-purple" icon={<IconFile />} disabled />
            </Tooltip>
          );
        }

        return (
          <IconButton
            size="md"
            variant="ghost-purple"
            key={index}
            icon={<IconFile />}
            onClick={() => {
              showFilePreviewModal({
                formFile: file,
                filesList: val,
              });
            }}
          />
        );
      })}
      {isTooLong && (
        <IconButton
          size="md"
          icon={<>+{val.length - thresholdLength}</>}
          variant="ghost-purple"
          onClick={() => column.onMultiFilesClick?.(row)}
        />
      )}
    </Stack>
  );
};

const editableText = (
  value: any,
  column: AvailableColumn<any>,
  row: DataItemType,
  maxLength?: number,
) => {
  return (
    <Button
      variant="ghost"
      onClick={() =>
        showColumnDetailModal({
          onSave: column.onEdit ? (newValue) => column.onEdit?.(row, newValue) : undefined,
          defaultValue: value,
          maxLength,
        })
      }
    >
      <SimpleText text={value} />
    </Button>
  );
};

const renderArrayField = (value: any) => {
  const { t } = getScopedTranslate(arrayLocale);
  if (!Array.isArray(value)) {
    return <SimpleText text="" />;
  }
  const previewText = `${value.length} ${value.length === 1 ? t('item') : t('items')}`;
  return (
    <Button
      variant="ghost"
      onClick={() =>
        showColumnDetailModal({
          defaultValue: JSON.stringify(value, null, 2),
        })
      }
    >
      <SimpleText text={previewText} />
    </Button>
  );
};

export const typeRenderMap: Record<
  ColumnType,
  (value: any, column: AvailableColumn<any>, row: DataItemType) => JSX.Element
> = {
  text: (...props) => editableText(...props, textColumnMaxLength),
  paragraph: editableText,
  number: (value) => <Number value={value} />,
  decimal: (value) => <Number value={value} />,
  email: (value) => <SimpleText text={value} />,
  phone_number: (value) => <PhoneNumber phoneNumber={value} />,
  single_select: (value) => <SimpleText text={value.label} />,
  multi_select: (value) => <SimpleText text={String(accessorMap.multi_select(value))} />,
  user: (value) => <SimpleText text={value.name || value.email || value.shortId} />,
  boolean: (value) => {
    return (
      <SimpleText text={value === true ? t('core.yes') : value === false ? t('core.no') : ''} />
    );
  },
  progress: (value) => <ProgressBar ratio={value} />,
  date: (value) => <SimpleText text={abbreviateDate(value, 'en')} />,
  datetime: (value) => <SimpleText text={abbreviateDate(value, 'en', 'datetime')} />,
  file: (value, column, row) => <FileField value={value} column={column} row={row} />,
  file_multi: (value, column, row) => <FileField value={value} column={column} row={row} />,
  issues: (value) => <SimpleText text={String(accessorMap.multi_select(value))} />,
  custom: () => <></>,
  array: renderArrayField,
  array_of_objects: renderArrayField,
};

const extractFileNames = (files?: File[]) => {
  if (!files || !Array.isArray(files)) {
    return '';
  }
  return files.map((file) => file.name).join(';');
};

const getArrayAccessorValue = (value: any) => {
  if (!Array.isArray(value)) {
    return 0;
  }
  return value.length;
};

export const accessorMap: Record<
  ColumnType,
  (value: any, column?: AvailableColumn<any>) => string | number | undefined
> = {
  text: (value, column) => value,
  number: (value, column) => value,
  decimal: (value, column) => value,
  paragraph: (value, column) => value,
  email: (value, column) => value,
  phone_number: (value, column) => {
    return formatPhoneNumber(value);
  },
  single_select: (value, column) => {
    if (typeof value === 'object') {
      return value.label;
    }
    if (column?.labelMap) {
      return column.labelMap[value] || value;
    }
  },
  multi_select: (value, column) => {
    if (!Array.isArray(value)) {
      return;
    }

    return sortStrings(value.map((item) => item.label)).join(', ');
  },
  user: (value, column) => value?.name,
  boolean: (value, column) => {
    return value === true ? t('core.yes') : value === false ? t('core.no') : undefined;
  },
  progress: (value, column) => {
    if (typeof value === 'number' && value >= 0 && value <= 1) {
      return `${value * 100}%`;
    }
  },
  date: (value, column) => value,
  datetime: (value, column) => value,
  file: (value, column) => extractFileNames(value),
  file_multi: (value, column) => extractFileNames(value),
  issues: (value, column) => {
    const { warnings, errors } = value;
    const issueMessages = [];

    if (warnings.length > 0) {
      issueMessages.push(`${warnings.length} ${warnings.length === 1 ? 'Warning' : 'Warnings'}`);
    }

    if (errors.length > 0) {
      issueMessages.push(`${errors.length} ${errors.length === 1 ? 'Error' : 'Errors'}`);
    }
    return issueMessages.length > 0 ? issueMessages.join(', ') : 'No Issues';
  },
  array: getArrayAccessorValue,
  array_of_objects: getArrayAccessorValue,
  custom: () => '',
};

export const AvatarName = ({ name, ...rest }: AvatarProps) => (
  <Stack orientation="horizontal" space={2} align="center">
    <Avatar name={name} {...rest} />
    <Text text={name} font="regular14" />
  </Stack>
);

type NameCellProps = {
  name: string;
  avatar?: ReactNode;
  trailing?: ReactNode;
};

export const NameCellLink = ({ name, avatar, trailing, to }: NameCellProps & { to: To }) => (
  <NameCellRow>
    {avatar}
    <ActionText.Link fill font="regular14" color="textPrimary" to={to}>
      <NameText>{name}</NameText>
      {trailing}
    </ActionText.Link>
  </NameCellRow>
);

export const NameCellButton = ({
  name,
  avatar,
  trailing,
  onClick,
}: NameCellProps & { onClick(): void }) => (
  <NameCellRow>
    {avatar}
    <ActionText fill font="regular14" color="textPrimary" onClick={onClick}>
      <NameText>{name}</NameText>
      {trailing}
    </ActionText>
  </NameCellRow>
);

const NameText = styled.span({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const NameCellRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  alignSelf: 'stretch',
  width: '100%',
  minWidth: 0,
});

export const NotSetText = ({
  onSave,
  maxLength,
}: Pick<ColumnDetailModalProps, 'onSave' | 'maxLength'>) => {
  const { t } = useScopedTranslate();
  const content = <Text text={t('core.not_set')} color="textHelper" font="regular14" />;

  if (!onSave) {
    return content;
  }

  return (
    <Button
      variant="ghost"
      onClick={() => showColumnDetailModal({ onSave, defaultValue: '', maxLength })}
    >
      {content}
    </Button>
  );
};

const arrayLocale = {
  en: {
    item: 'item',
    items: 'items',
  },
};
